import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { STATES_DATA, getStateBySlug, slugify } from "@/data/states";
import { USAMap } from "@/components/USAMap";
import { GeoSections } from "@/components/GeoSections";
import { buildLocationContent, breadcrumbSchema, geoCoords } from "@/data/geo-content";
import { getStateHighlight } from "@/data/state-highlights";
import { ogImageMeta } from "@/lib/og-image";
import { leafCanonical } from "@/lib/canonical";


export const Route = createFileRoute("/states/$state")({
  loader: ({ params }) => {
    const s = getStateBySlug(params.state);
    if (!s) throw notFound();
    return s;
  },
  head: ({ loaderData, match, matches }) => {
    const s = loaderData!;
    const content = buildLocationContent({
      locationName: s.name,
      stateName: s.name,
      stateCode: s.code,
      scope: "state",
    });
    const title = `Free Vending Machines in ${s.name} — $0 Install`;
    const desc = `FREE vending machines for ${s.name} businesses — zero-cost install, restocking & service across ${s.cities.slice(0, 3).join(", ")} and all of ${s.code}. We try to respond in 4–6 hrs.`;
    const url = `https://vendingmachinefree.com/states/${s.slug}`;
    const coords = geoCoords(s.code, s.name);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...ogImageMeta(),
      ],
      links: leafCanonical(url, { match, matches }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Vending Machine Free.com",
              url: "https://vendingmachinefree.com",
              logo: "https://vendingmachinefree.com/favicon-512.png",
              slogan: "Powered by American businesses — get a Free Vending Machine Nationwide.",
              areaServed: { "@type": "Country", name: "United States" },
            },
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              name: `Vending Machine Free.com — ${s.name}`,
              areaServed: { "@type": "State", name: s.name },
              description: desc,
              url,
              geo: { "@type": "GeoCoordinates", latitude: coords.lat, longitude: coords.lng },
            },
            {
              "@context": "https://schema.org",
              "@type": "Service",
              serviceType: "Free Vending Machine Placement",
              areaServed: { "@type": "State", name: s.name },
              provider: { "@type": "Organization", name: "Vending Machine Free.com", url: "https://vendingmachinefree.com" },
            },
            breadcrumbSchema([
              { name: "Home", url: "https://vendingmachinefree.com/" },
              { name: "USA Coverage", url: "https://vendingmachinefree.com/coverage" },
              { name: s.name, url },
            ]),
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: content.faqs.map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
          ]),
        },
      ],
    };
  },
  component: StatePage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <Link to="/" className="text-safety">
        Go Home
      </Link>
    </div>
  ),
  errorComponent: () => <div className="p-10">Error loading state.</div>,
});

function StatePage() {
  const s = Route.useLoaderData();
  const content = buildLocationContent({
    locationName: s.name,
    stateName: s.name,
    stateCode: s.code,
    scope: "state",
  });
  const highlight = getStateHighlight(s.slug);
  return (

    <div className="min-h-screen bg-background">
      <PageHeader />
      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x">
          <nav className="text-xs text-white/60 mb-4">
            <Link to="/" className="hover:text-safety">
              Home
            </Link>{" "}
            /{" "}
            <Link to="/coverage" className="hover:text-safety">
              USA Map
            </Link>{" "}
            / <span className="text-white">{s.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <MapPin className="h-4 w-4" /> {s.code} · Active Coverage
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Free Vending Machines in {s.name}
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl">
            We supply, install, restock, and service modern vending machines for businesses across{" "}
            {s.name} — 100% FREE. Local route operators based throughout{" "}
            {s.cities.slice(0, 2).join(" and ")}. We try to respond in less than 4hrs – 6hrs.
          </p>
          <a
            href="#apply"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            Get FREE Vending Machines in {s.name} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {highlight && (
        <section className="py-14 bg-secondary/40 border-b border-border">
          <div className="container-x">
            <h2 className="font-display text-3xl md:text-4xl uppercase">{highlight.headline}</h2>
            <p className="mt-3 text-muted-foreground max-w-3xl">{highlight.intro}</p>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
              {highlight.stats.map((st) => (
                <div key={st.label} className="rounded-lg border border-border bg-background p-4">
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">{st.label}</dt>
                  <dd className="mt-1 font-display text-2xl text-safety">{st.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="font-display text-xl uppercase">Top Metros in {s.name}</h3>
                <ul className="mt-4 space-y-3">
                  {highlight.topMetros.map((m) => (
                    <li key={m.name} className="rounded-md border border-border bg-background p-4">
                      <Link
                        to="/states/$state/$town"
                        params={{ state: s.slug, town: slugify(m.name) }}
                        className="font-semibold hover:text-safety"
                      >
                        {m.name}, {s.code}
                      </Link>
                      <p className="mt-1 text-sm text-muted-foreground">{m.blurb}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-display text-xl uppercase">Industries We Serve in {s.name}</h3>
                <ul className="mt-4 space-y-3">
                  {highlight.industries.map((ind) => (
                    <li key={ind.slug} className="rounded-md border border-border bg-background p-4">
                      <Link
                        to="/industries/$industry/$state"
                        params={{ industry: ind.slug, state: s.slug }}
                        className="font-semibold hover:text-safety"
                      >
                        {ind.label} — {s.name}
                      </Link>
                      <p className="mt-1 text-sm text-muted-foreground">{ind.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {highlight.extraFaqs.length > 0 && (
              <div className="mt-10 max-w-3xl">
                <h3 className="font-display text-xl uppercase">{s.name} FAQs</h3>
                <div className="mt-4 space-y-4">
                  {highlight.extraFaqs.map((f) => (
                    <div key={f.q} className="rounded-md border border-border bg-background p-4">
                      <div className="font-semibold">{f.q}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <GeoSections content={content} locationName={s.name} stateName={s.name} />



      <section className="py-14">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">
            Major Cities & Towns We Serve in {s.name}
          </h2>
          <p className="mt-2 text-muted-foreground">
            Click any city to view local coverage, neighborhoods, ZIP zones, and FAQs.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {s.cities.map((town: string) => (
              <Link
                key={town}
                to="/states/$state/$town"
                params={{ state: s.slug, town: slugify(town) }}
                title={`Free vending machines in ${town}, ${s.name}`}
                className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-safety hover:shadow-md transition"
              >
                <div className="h-10 w-10 rounded-md bg-steel/10 text-steel flex items-center justify-center group-hover:bg-safety/15 group-hover:text-safety transition">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold">{town}</div>
                  <div className="text-xs text-muted-foreground">{s.code} · Free placement</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container-x text-center flex flex-wrap justify-center gap-5 text-sm">
          <Link to="/coverage" className="text-steel hover:text-safety font-semibold">
            ← USA Map
          </Link>
          <Link to="/free-vending-machines-usa" className="text-steel hover:text-safety font-semibold">
            Free Vending Machines USA
          </Link>
          <Link to="/free-vending-machine-placement" className="text-steel hover:text-safety font-semibold">
            Free Placement
          </Link>
          <Link to="/vending-machine-placement" className="text-steel hover:text-safety font-semibold">
            Placement Service
          </Link>
          <Link to="/" className="text-steel hover:text-safety font-semibold">
            Home
          </Link>
          <Link to="/" hash="contact" className="text-steel hover:text-safety font-semibold">
            Apply Now
          </Link>
        </div>
      </section>

      <section className="py-12 bg-charcoal text-charcoal-foreground">
        <div className="container-x">
          <h3 className="font-display text-xl uppercase text-center mb-6">
            Explore Coverage in Other States
          </h3>
          <USAMap compact />
        </div>
      </section>
    </div>
  );
}

function PageHeader() {
  return (
    <header className="border-b border-border bg-background sticky top-0 z-40">
      <div className="container-x flex h-16 items-center justify-between">
        <Link to="/" className="font-display text-xl uppercase">
          Vending Machine Free<span className="text-safety">.com</span>
        </Link>
        <Link
          to="/"
          hash="contact"
          className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground"
        >
          Apply Free <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </header>
  );
}

export { STATES_DATA };
